import type { AgendaEvent } from "@domaddress/shared";

const DAY_LABELS: Record<string, string> = {
  mon: "maandag",
  tue: "dinsdag",
  wed: "woensdag",
  thu: "donderdag",
  fri: "vrijdag",
  sat: "zaterdag",
  sun: "zondag",
};

function formatNlDate(dateStr: string): string {
  const d = new Date(`${dateStr.slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
}

function weeklyDaysLabel(raw: string | null): string {
  if (!raw) return "";
  return raw
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .map((k) => DAY_LABELS[k] ?? k)
    .join(", ");
}

function endsLabel(ev: AgendaEvent): string {
  if (ev.recurrence_end_type === "after" && ev.recurrence_end_count > 0) {
    return `Eindigt na ${ev.recurrence_end_count} keer`;
  }
  if (ev.recurrence_end_type === "on_date" && ev.recurrence_end_date) {
    return `Eindigt op ${formatNlDate(ev.recurrence_end_date)}`;
  }
  return "Eindigt nooit";
}

/** Leesbare samenvatting van herhalingsinstellingen. */
export function describeRecurrence(ev: AgendaEvent): string {
  const type = ev.recurrence_type ?? "none";
  if (type === "none") return "Geen herhaling";

  const interval = ev.recurrence_interval > 1 ? ` elke ${ev.recurrence_interval}` : "";
  const ends = endsLabel(ev);

  if (type === "daily") {
    return `Dagelijks${interval.replace(" elke", ", elke")} · ${ends}`;
  }
  if (type === "weekly") {
    const days = weeklyDaysLabel(ev.recurrence_weekly_days);
    return `Wekelijks${interval}${days ? ` op ${days}` : ""} · ${ends}`;
  }
  if (type === "monthly") {
    return `Maandelijks${interval} · ${ends}`;
  }
  if (type === "yearly") {
    return `Jaarlijks${interval} · ${ends}`;
  }
  if (type === "specifieke_data") {
    const dates = ev.specific_dates ?? [];
    if (dates.length === 0) return "Op specifieke datums (nog geen datums)";
    return `Op de volgende datums: ${dates.map(formatNlDate).join(", ")}`;
  }
  return type;
}

export function isRecurring(ev: AgendaEvent): boolean {
  return (ev.recurrence_type ?? "none") !== "none";
}
